import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { parseISO, format, isSameDay } from 'date-fns'

import { ReserveProps, ReserveContainerProps } from './Reserve.types'
import { pricingLabels } from '../../FairDetail.types'
import { Button } from '../../../../components/ui-elements/Button'
import { DateTimeInput } from '../../../../components/ui-elements/DateTimeInput'
import { Availability } from '../../../../components/ui-elements/Icons/Availability'
import { styleTheme } from '../../../../style/theme'
import { addMinutes, parse } from 'date-fns'

const addMinutesToTime = (time: string, minutesToAdd: number): string => {
  const parsedTime = parse(time, 'HH:mm', new Date())
  const newTime = addMinutes(parsedTime, minutesToAdd)
  return newTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export const Reserve: React.FC<ReserveContainerProps> = (props) => {
  return <StyledPage {...props} />
}

const availabilityText = (availability: 'none' | 'few' | 'lot') => {
  switch (availability) {
    case 'none':
      return '満席'
    case 'few':
      return '残りわずか'
    case 'lot':
      return '空席あり'
  }
}

const Page: React.FC<ReserveProps> = ({
  className,
  primaryColor,
  fairTimes,
  initialFairDate,
  pricing,
  price,
  numberOfPeople,
  pricingDescriptionText,
  setBridalFairBaseByDate,
  minutes,
}) => {
  const { bridalFairPageKey, bridalFairDetailKey } = useParams() as {
    bridalFairPageKey?: string
    bridalFairDetailKey: string
  }

  const [selectedDate, setSelectedDate] = useState<Date | null>(initialFairDate)

  const navigate = useNavigate()

  const handleReserveClick = (fairTimeKey: string) => {
    const path = bridalFairPageKey
      ? `/customer/public/bridal_fair_pages/${bridalFairPageKey}/fairs/${bridalFairDetailKey}/fair_times/${fairTimeKey}/participations/new`
      : `/customer/public/bridal_fair_details/${bridalFairDetailKey}/fair_times/${fairTimeKey}`

    navigate(path)
  }

  function convertToJpnDate(dateString) {
    const date = new Date(dateString)
    const jpnDateTime = new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Tokyo',
    })

    const formatted = jpnDateTime.format(date).split(' ')
    const dateParts = formatted[0].split('/').map((part) => parseInt(part, 10))

    return {
      fairDate: new Date(dateParts[0], dateParts[1] - 1, dateParts[2]),
      fairTime: formatted[1],
    }
  }

  const fairScheduleList = Object.values(fairTimes).map((entry) => {
    const { fairDate, fairTime } = convertToJpnDate(entry.startAt)
    return {
      date: fairDate,
      time: fairTime,
      availability: entry.availability,
      fairTimeKey: format(parseISO(entry.startAt), 'yyyyMMddHHmm'),
    }
  })

  const datesToInclude = fairScheduleList.map((entry) => entry.date)

  const scheduleList = selectedDate ? fairScheduleList.filter((schedule) => isSameDay(schedule.date, selectedDate)) : []

  return (
    <div className={className}>
      <div className="date">
        <DateTimeInput
          onChange={(reserveDate) => {
            setSelectedDate(reserveDate)
            reserveDate && setBridalFairBaseByDate(reserveDate)
          }}
          includeDates={datesToInclude}
          selected={selectedDate}
          readonly
        />
        {pricing && (
          <div className="pricing">
            <div className="pricing-info">
              <span className="pricing-tag">{pricingLabels[pricing]}</span>
              {(pricing === 'paid' || pricing === 'partially_paid') && (price || numberOfPeople) && (
                <span className="pricing-price">
                  {price && <>{price.toLocaleString()}円</>}
                  {price && numberOfPeople && <>/</>}
                  {numberOfPeople && <>{numberOfPeople}名</>}
                </span>
              )}
            </div>
            {(pricing === 'paid' || pricing === 'partially_paid') && pricingDescriptionText && (
              <p className="pricing-description">
                <span>{`※ ${pricingDescriptionText}`}</span>
              </p>
            )}
          </div>
        )}
      </div>
      {scheduleList.length > 0 ? (
        <ul className="list">
          {scheduleList.map((schedule) => {
            return (
              <li key={schedule.time} className="item">
                <div className="info">
                  <p className="info__time">{`${schedule.time}${
                    minutes && `~${addMinutesToTime(schedule.time, minutes)}`
                  }`}</p>
                  <div className="info__status">
                    <Availability availability={schedule.availability} />
                    {availabilityText(schedule.availability)}
                  </div>
                </div>
                <div className="button">
                  <Button
                    color={primaryColor}
                    onClick={() => handleReserveClick(schedule.fairTimeKey)}
                    disabled={schedule.availability === 'none'}
                  >
                    予約する
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      ) : (
        <div className="no-list">
          <span>{selectedDate && '別の'}日付を選択してください。</span>
        </div>
      )}
    </div>
  )
}

const StyledPage = styled(Page)`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  color: ${styleTheme.colors.black};

  @media screen and (min-width: 768px) {
    grid-template-columns: 240px 1fr;
  }

  > .date {
    margin-bottom: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    .pricing {
      display: flex;
      flex-direction: column;
      gap: 8px;

      &-info {
        display: flex;
        gap: 4px;
      }

      &-tag {
        font-size: 12px;
        color: ${styleTheme.colors.black};
        border: 1px solid ${styleTheme.colors.black};
        padding: 2px 8px;
        border-radius: 2px;
        line-height: 1.5;
        display: inline-block;
      }

      &-description {
        font-size: 12px;
        display: flex;
        gap: 2px;
      }
    }

    .pricing-info {
      display: flex;
      gap: 16px;
    }
  }

  > .list {
    > .item {
      display: flex;
      width: 100%;
      padding: 16px 0;
      border-top: 1px solid #e6e6e6;
      align-items: center;
      justify-content: space-between;

      @media screen and (min-width: 768px) {
        gap: 24px;
        padding-left: 60px;
      }

      &:last-child {
        border-bottom: 1px solid #e6e6e6;
      }

      > .info {
        line-height: 1.5;

        @media screen and (min-width: 521px) {
          display: flex;
          gap: 24px;
          align-items: center;
        }

        > .info__time {
          font-size: 18px;
          font-weight: bold;
        }

        > .info__status {
          font-size: 14px;
          display: flex;
          gap: 2px;
          align-items: center;
        }
      }

      > .button {
        width: 160px;
      }
    }
  }

  > .no-list {
    display: flex;
    flex-direction: column;
  }
`
